import React, { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import isNil from "lodash/isNil";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import ReturnCreditsSummary from "./components/ReturnCreditsSummary";
import { CommonEvent } from "@narvar/nth-kit-returns-headless";
import { ReviewSummaryPayload } from "../ReviewSummary/types";
import { useStyles } from "./styles";
import useTransformToNth from "./hooks/useTransformToNth";
import useConsumerSettings from "../hooks/useConsumerSettings";
import CheckoutForm from "./components/CheckoutForm";
import usePayment from "./hooks/usePayment";
import useSubmit from "./hooks/useSubmit";
import { useDiscountCode } from "./hooks/useDiscountCode";
import { redirectToShop } from "../../shared/modules/frame";
import DiscountCode from "./components/DiscountCode";
import { config } from "../config";
import { usePreferences } from "./hooks/usePreferences";
import { ReviewSummaryRaw } from "../ReviewSummary";
import { getTranslation } from "../../shared/modules/config";
import { interpolate } from "../../shared/modules/template";
import useCheckout from "./hooks/useCheckout";

const Checkout = () => {
  const classes = useStyles();

  const [searchParams] = useSearchParams();
  const code = searchParams.get("c") ?? "";
  const token = searchParams.get("t") ?? "";
  const { localeFull, includeCurrency } = useConsumerSettings();

  const {
    checkoutResponse,
    loadingCheckoutData,
    refetchCheckoutData,
    checkoutData,
    paymentClientSecret,
    paymentRequired,
    paymentProvider,
    paymentProcessorPubKey,
    paymentProcessorId,
    discountCodeSaved,
    paymentNarvarRefId,
  } = useCheckout(token, code);

  const onSuccessDiscountCodeApply = useCallback(
    async (discountCode: string) => {
      const { data } = await refetchCheckoutData();
      const discountCodes =
        data?.shopNowV2Checkout?.data?.overallCartSummary.discountCodes ?? [];
      if (discountCode && !discountCodes.includes(discountCode)) {
        throw new Error(
          interpolate(
            getTranslation("shop_now_checkout_discount_code_not_valid"),
            {
              code: discountCode,
            },
          ),
        );
      }
    },
    [refetchCheckoutData],
  );

  const onSuccessDiscountCodeRemove = useCallback(async () => {
    await refetchCheckoutData();
  }, [refetchCheckoutData]);

  const {
    discountCode,
    applyDiscount,
    removeDiscount,
    discountError,
    discountLoading,
    discountCodeApplyError,
  } = useDiscountCode({
    code,
    token,
    discountCodeSaved,
    onSuccessApply: onSuccessDiscountCodeApply,
    onSuccessRemove: onSuccessDiscountCodeRemove,
  });

  const {
    preferencesStatus,
    savePreferences,
    setSavePreferences,
    updateConsumerPreferences,
  } = usePreferences();

  const {
    paymentError,
    makePayment,
    capturingPayment,
    isPaymentConfigurationError,
    eligiblePaymentProvider,
  } = usePayment({
    secret: paymentClientSecret,
    paymentProvider,
    paymentNarvarRefId,
    paymentRequired,
  });

  const reviewSummaryProps = useTransformToNth(checkoutData);

  const { loadingSubmitCheckout, onSubmitHandler, submitError } = useSubmit({
    paymentProcessorId,
    paymentProvider: eligiblePaymentProvider,
    makePayment,
    code,
    updateConsumerPreferences,
    cartToken: token,
  });

  const checkoutErrorCode = checkoutResponse?.shopNowV2Checkout.errors[0]?.code;

  useEffect(() => {
    if (checkoutErrorCode === "Z501") {
      redirectToShop();
    }
  }, [checkoutErrorCode]);

  const errorMessage =
    submitError ??
    checkoutResponse?.shopNowV2Checkout.errors[0]?.message ??
    discountError;

  const handleReviewSummaryChange: CommonEvent<ReviewSummaryPayload, string> = (
    event,
  ) => {
    if (!isNil(event.value?.savePreferences)) {
      setSavePreferences(event.value.savePreferences);
    }
  };

  return (
    <Grid className={classes.root} spacing={2} container>
      <Grid xs={12} item>
        <ReviewSummaryRaw
          locale={localeFull}
          includeCurrency={includeCurrency}
          error={errorMessage}
          loading={loadingCheckoutData}
          submitting={capturingPayment || loadingSubmitCheckout}
          disabled={
            discountLoading ||
            capturingPayment ||
            loadingCheckoutData ||
            isPaymentConfigurationError
          }
          allowRetry
          hasAssociatedEmail
          savePreferences={config.isUserPreferencesEnabled && savePreferences}
          preferencesStatus={preferencesStatus}
          {...reviewSummaryProps}
          hasClaim={false}
          onChange={handleReviewSummaryChange}
          returnCreditsSummary={
            <ReturnCreditsSummary
              returnCartSummary={checkoutData?.returnCart}
            />
          }
          renderAddOn={
            <>
              {!config.isShopNowDiscountCodeDisabled && (
                <DiscountCode
                  discountCode={discountCode}
                  onApply={applyDiscount}
                  onRemove={removeDiscount}
                  discountLoading={discountLoading}
                  error={discountCodeApplyError}
                />
              )}
              {paymentRequired && (
                <CheckoutForm
                  secret={paymentClientSecret}
                  hideSubmit
                  submitLoading={capturingPayment}
                  paymentProvider={eligiblePaymentProvider}
                  backendPaymentFailureError={paymentError}
                  paymentProcessorPubKey={paymentProcessorPubKey}
                />
              )}
              {isPaymentConfigurationError && (
                <Typography
                  align="center"
                  variant="body1"
                  className={classes.errorText}>
                  {getTranslation(
                    "shop_now_checkout_payment_invalid_configuration",
                  )}
                </Typography>
              )}
              {config.isShopNowCheckoutDisclaimerEnabled && (
                <Typography align="center" variant="body1">
                  {getTranslation("shop_now_checkout_exchange_order_summary")}
                </Typography>
              )}
            </>
          }
          onBack={redirectToShop}
          onSubmit={onSubmitHandler}
        />
      </Grid>
    </Grid>
  );
};

export default Checkout;
